import { APP_VERSION } from "@/appVersion";
import { mcpBridgeStore } from "@/mcp/mcpBridgeStore";
import { dispatchMcpTool } from "@/mcp/toolHandlers";
import {
  MCP_BRIDGE_URL,
  MCP_PROTOCOL_VERSION,
  parseMcpBridgeMessage,
  type McpBridgeHostMessage,
} from "@/mcp/protocol";
import {
  readMcpHostConnectPreference,
  writeMcpHostConnectPreference,
} from "@/editor/shell/editorPreferences";

const RECONNECT_BASE_MS = 800;
const RECONNECT_MAX_MS = 8_000;
const PING_INTERVAL_MS = 15_000;

let socket: WebSocket | undefined;
let enabled = false;
let reconnectAttempt = 0;
let reconnectTimer: number | undefined;
let pingTimer: number | undefined;
let intentionalClose = false;

function clearTimers(): void {
  if (reconnectTimer !== undefined) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
  }
  if (pingTimer !== undefined) {
    window.clearInterval(pingTimer);
    pingTimer = undefined;
  }
}

function setStatus(
  status: "disconnected" | "connecting" | "connected" | "error",
  extras: { sessionId?: string; lastError?: string } = {},
): void {
  mcpBridgeStore.patch({
    enabled,
    status,
    sessionId: extras.sessionId,
    lastError: extras.lastError,
  });
}

function scheduleReconnect(): void {
  if (!enabled || intentionalClose) {
    return;
  }
  clearTimers();
  const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** reconnectAttempt);
  reconnectAttempt += 1;
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = undefined;
    openSocket();
  }, delay);
}

function startPing(): void {
  if (pingTimer !== undefined) {
    window.clearInterval(pingTimer);
  }
  pingTimer = window.setInterval(() => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: "ping", t: Date.now() }));
    }
  }, PING_INTERVAL_MS);
}

function sendToHost(message: object): void {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message));
  }
}

async function handleRequest(message: Extract<McpBridgeHostMessage, { type: "request" }>): Promise<void> {
  try {
    const result = await dispatchMcpTool(message.method, message.params ?? {});
    sendToHost({ type: "response", id: message.id, ok: true, result });
  } catch (error) {
    sendToHost({
      type: "response",
      id: message.id,
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

function handleHostMessage(message: McpBridgeHostMessage): void {
  if (message.type === "welcome") {
    reconnectAttempt = 0;
    setStatus("connected", { sessionId: message.sessionId, lastError: undefined });
    startPing();
    return;
  }
  if (message.type === "pong") {
    return;
  }
  if (message.type === "error") {
    setStatus("error", { lastError: message.message });
    return;
  }
  if (message.type === "request") {
    void handleRequest(message);
  }
}

function openSocket(): void {
  if (!enabled) {
    return;
  }
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }

  intentionalClose = false;
  setStatus("connecting", { sessionId: undefined, lastError: undefined });

  let next: WebSocket;
  try {
    next = new WebSocket(MCP_BRIDGE_URL);
  } catch (error) {
    setStatus("error", {
      lastError: error instanceof Error ? error.message : "Failed to open WebSocket",
    });
    scheduleReconnect();
    return;
  }

  socket = next;

  next.addEventListener("open", () => {
    if (socket !== next) {
      return;
    }
    next.send(JSON.stringify({
      type: "hello",
      protocolVersion: MCP_PROTOCOL_VERSION,
      client: "curva-editor",
      appVersion: APP_VERSION,
    }));
  });

  next.addEventListener("message", event => {
    if (socket !== next) {
      return;
    }
    const text = typeof event.data === "string" ? event.data : undefined;
    if (!text) {
      return;
    }
    const message = parseMcpBridgeMessage(text);
    if (!message || message.type === "hello" || message.type === "ping" || message.type === "response") {
      return;
    }
    handleHostMessage(message);
  });

  next.addEventListener("close", () => {
    if (socket !== next) {
      return;
    }
    socket = undefined;
    clearTimers();
    if (intentionalClose || !enabled) {
      setStatus("disconnected", { sessionId: undefined });
      return;
    }
    setStatus("error", { sessionId: undefined, lastError: "Disconnected from MCP host" });
    scheduleReconnect();
  });

  next.addEventListener("error", () => {
    // `close` follows; reconnect is scheduled there.
  });
}

function closeSocket(): void {
  intentionalClose = true;
  clearTimers();
  const current = socket;
  socket = undefined;
  if (current && current.readyState < WebSocket.CLOSING) {
    current.close();
  }
  setStatus("disconnected", { sessionId: undefined, lastError: undefined });
}

/** Apply the connect preference and open/close the socket accordingly. */
export function setMcpHostConnectEnabled(nextEnabled: boolean): void {
  enabled = nextEnabled;
  writeMcpHostConnectPreference(nextEnabled);
  mcpBridgeStore.patch({ enabled: nextEnabled });

  if (nextEnabled) {
    reconnectAttempt = 0;
    openSocket();
  } else {
    closeSocket();
  }
}

export function toggleMcpHostConnect(): void {
  setMcpHostConnectEnabled(!mcpBridgeStore.getState().enabled);
}

/**
 * Call once from the editor shell. Restores the localStorage preference and
 * auto-connects when it was left on.
 */
export function initMcpBridgeFromPreference(): void {
  const preference = readMcpHostConnectPreference();
  enabled = preference;
  mcpBridgeStore.patch({ enabled: preference });
  if (preference) {
    reconnectAttempt = 0;
    openSocket();
  }
}

export function getMcpBridgeUrl(): string {
  return MCP_BRIDGE_URL;
}
